import React, { useState, useCallback } from 'react';

import EditorComponent from '~/common/editor/_Editor';
import Controls from './Controls';

interface EditorProps {
    template?: any;
    isInAction?: boolean;
    creativeName?: string;
    videosCount?: number;
    saved?: boolean;
    hasCorruptionReason?: boolean;
    setSaveStatus?: (...args: any[]) => void;
    handleOnSave: (...args: any[]) => void;
    handleOnDiscard: (...args: any[]) => void;
    handleOnPublish: (...args: any[]) => void;
    saveAsTemplate: (...args: any[]) => void;
    saveAsVideo: (...args: any[]) => void;
    [key: string]: any;
}

interface EditorState {
    showDiscardModal: boolean;
    showPublishModal: boolean;
    showSaveAsTemplateModal: boolean;
    showSaveAsVideoModal: boolean;
    showSaveAsVideoNotAcceptableErrorModal: boolean;
    creativeVersionName: string;
}

export default function Editor(props: EditorProps) {
    const { template, isInAction, creativeName, videosCount, saved, hasCorruptionReason, setSaveStatus } = props;

    const [state, setState] = useState<EditorState>({
        showDiscardModal: false,
        showPublishModal: false,
        showSaveAsTemplateModal: false,
        showSaveAsVideoModal: false,
        showSaveAsVideoNotAcceptableErrorModal: false,
        creativeVersionName: '',
    });

    const save = useCallback(
        (
            creativeModel: any,
            exportIntegrations: any,
            exportConfigurationIds: any,
            approvalEnabled: any,
            reviewers: any,
            silent: any,
        ) => {
            props.handleOnSave(
                creativeModel,
                exportIntegrations,
                exportConfigurationIds,
                approvalEnabled,
                reviewers,
                silent,
            );
        },
        [props.handleOnSave],
    );

    const discard = useCallback(() => {
        setState((prev) => ({ ...prev, showDiscardModal: false }));
        props.handleOnDiscard();
    }, [props.handleOnDiscard]);

    const publish = useCallback(
        (
            creativeModel: any,
            exportIntegrations: any,
            exportConfigurationIds: any,
            approvalEnabled: any,
            reviewers: any,
        ) => {
            props.handleOnPublish(
                creativeModel,
                exportIntegrations,
                exportConfigurationIds,
                approvalEnabled,
                reviewers,
            );
            setState((prev) => ({ ...prev, creativeVersionName: '' }));
        },
        [props.handleOnPublish],
    );

    const saveAsTemplate = useCallback(
        (tmplData: any) => {
            props.saveAsTemplate(tmplData);
            setState((prev) => ({ ...prev, showSaveAsTemplateModal: false }));
        },
        [props.saveAsTemplate],
    );

    const saveAsVideoModal = useCallback(
        () => (name: any) => {
            props.saveAsVideo(name, (errorPayload: any) => {
                if (errorPayload?.appErrorCode === 11001) {
                    setState((prev) => ({ ...prev, showSaveAsVideoNotAcceptableErrorModal: true }));
                }
            });
            setState((prev) => ({ ...prev, showSaveAsVideoModal: false }));
        },
        [props.saveAsVideo],
    );

    const handleDiscardModal = useCallback((show: boolean) => {
        setState((prev) => ({ ...prev, showDiscardModal: show }));
    }, []);

    const handlePublishModal = useCallback((show: boolean) => {
        setState((prev) => ({ ...prev, showPublishModal: show }));
    }, []);

    const closePublishModal = useCallback(() => {
        setState((prev) => ({ ...prev, showPublishModal: false }));
    }, []);

    const handleSaveAsTemplateModal = useCallback((show: boolean) => {
        setState((prev) => ({ ...prev, showSaveAsTemplateModal: show }));
    }, []);

    const handleSaveAsVideoModal = useCallback((show: boolean) => {
        setState((prev) => ({ ...prev, showSaveAsVideoModal: show }));
    }, []);

    const closeSaveAsVideoNotAcceptableErrorModal = useCallback(() => {
        setState((prev) => ({ ...prev, showSaveAsVideoNotAcceptableErrorModal: false }));
    }, []);

    const handleNameChange = useCallback((value: any) => {
        setState((prev) => ({ ...prev, creativeVersionName: value }));
    }, []);

    if (!template) return <div />;

    const controlsProps = {
        template,
        saved,
        discard,
        handlePublishModal,
        handleDiscardModal,
        save,
        publish,
        handleNameChange,
        isInAction,
        videosCount,
        handleSaveAsVideoModal,
        closeSaveAsVideoNotAcceptableErrorModal,
        handleSaveAsTemplateModal,
        saveAsTemplate,
        saveAsVideoModal,
        creativeName,
        hasCorruptionReason,
        setSaveStatus,
        closePublishModal,
        ...state,
    };

    return (
        <EditorComponent key="spec_key" handleOnResize={() => null} {...props} controlsProps={controlsProps}>
            <Controls {...controlsProps} />
        </EditorComponent>
    );
}
